/**
 * Factor registry: one entry per analysis-config factor toggle.
 *
 * One place for a factor's label, default, snapshot column, gating matrix key and
 * deprecation, so counting, the tier clamp and each app do not keep their own copies.
 */

import type { AnalysisConfig } from './analysisConfig';
import { type GatedFeature, getAccessPattern } from './featureAccess';
import { SUBSCRIPTION_TIERS, type SubscriptionTier } from './subscriptionTier';

export const FACTOR_SECTIONS = [
  'playerStats',
  'advancedStats',
  'matchupData',
  'opponentDefense',
  'gameScript',
  'efficiencyMetrics',
  'otherFactors',
] as const;

export type FactorSection = (typeof FACTOR_SECTIONS)[number];

export interface FactorDefinition {
  /** `section.key`, the path of the toggle in AnalysisConfig. */
  id: string;
  section: FactorSection;
  key: string;
  label: string;
  description: string;
  defaultEnabled: boolean;
  /** pick_predictions column recording whether it was on; null when none exists. */
  snapshotColumn: string | null;
  /** Matrix key a tier needs for this toggle; none means every tier. */
  feature?: GatedFeature;
  /** Set when the factor is retired from use; the toggle stays admin-only and disabled. */
  deprecatedReason?: string;
  /** Only meaningful for leagues with player props. */
  playerPropsOnly: boolean;
  /** External source the factor's data comes from alone; when it is off, the factor is off. */
  dataSource?: FactorDataSource;
}

/** Optional external data sources a factor can depend on alone. */
export type FactorDataSource = 'msf';

/** Which optional sources are on (the server reports them in /api/health). */
export type FactorDataSources = Partial<Record<FactorDataSource, boolean>>;

function factor(
  section: FactorSection,
  key: string,
  label: string,
  description: string,
  snapshotColumn: string | null,
  extra: Partial<
    Pick<
      FactorDefinition,
      'defaultEnabled' | 'feature' | 'deprecatedReason' | 'playerPropsOnly' | 'dataSource'
    >
  > = {},
): FactorDefinition {
  return {
    id: `${section}.${key}`,
    section,
    key,
    label,
    description,
    snapshotColumn,
    defaultEnabled: extra.defaultEnabled ?? false,
    feature: extra.feature,
    deprecatedReason: extra.deprecatedReason,
    playerPropsOnly: extra.playerPropsOnly ?? false,
    dataSource: extra.dataSource,
  };
}

const ON = { defaultEnabled: true } as const;
const PLAYER = { playerPropsOnly: true } as const;

export const FACTOR_REGISTRY: readonly FactorDefinition[] = [
  // Player stats
  factor('playerStats', 'l5Average', 'L5 Average', 'Last 5 games average', 'factor_l5_enabled', {
    ...ON,
    ...PLAYER,
  }),
  factor(
    'playerStats',
    'l10Average',
    'L10 Average',
    'Last 10 games average',
    'factor_l10_enabled',
    { ...ON, ...PLAYER },
  ),
  factor(
    'playerStats',
    'l20Average',
    'L20 Average',
    'Last 20 games average',
    'factor_l20_enabled',
    PLAYER,
  ),
  factor(
    'playerStats',
    'seasonAverage',
    'Season Average',
    'Full season baseline',
    'factor_season_enabled',
    { ...ON, ...PLAYER },
  ),

  // Advanced stats
  factor(
    'advancedStats',
    'usageRate',
    'Usage Rate %',
    'How often player has the ball',
    'factor_usage_rate_enabled',
    {
      ...PLAYER,
      deprecatedReason: 'Neutral: 48.96% vs 48.93% baseline, no measurable benefit',
    },
  ),
  factor(
    'advancedStats',
    'trueShootingPct',
    'True Shooting %',
    'Scoring efficiency metric',
    'factor_true_shooting_enabled',
    {
      ...PLAYER,
      deprecatedReason: 'Neutral: 48.78% vs 48.95% baseline, no measurable benefit',
    },
  ),
  factor(
    'advancedStats',
    'assistRatio',
    'Assist Ratio',
    'Playmaking tendency (AST/TO ratio)',
    'factor_assist_ratio_enabled',
    PLAYER,
  ),
  factor(
    'advancedStats',
    'offensiveRating',
    'Offensive Rating',
    'Team points per 100 possessions',
    'factor_offensive_rating_enabled',
    PLAYER,
  ),
  factor(
    'advancedStats',
    'defensiveRating',
    'Defensive Rating',
    'Team points allowed per 100 possessions',
    'factor_defensive_rating_enabled',
    PLAYER,
  ),
  factor(
    'advancedStats',
    'l10TeamRatings',
    'L10 Team Ratings',
    "Each team's points scored and allowed over its last 10 games, against its average before them",
    'factor_l10_team_ratings_enabled',
  ),

  // Matchup
  factor(
    'matchupData',
    'playerVsTeamHistory',
    'Player vs Team History',
    'Avg stats vs this opponent (last 2 seasons)',
    'factor_vs_team_history_enabled',
    PLAYER,
  ),
  factor(
    'matchupData',
    'playerPosition',
    'Player Position',
    'PG/SG/SF/PF/C designation',
    'factor_player_position_enabled',
    PLAYER,
  ),

  // Opponent defense
  factor(
    'opponentDefense',
    'defVsPosition',
    'Shot Location Defense',
    "Opponent's FG% allowed on threes, in the paint and overall",
    'factor_def_vs_position_enabled',
    { ...ON, ...PLAYER },
  ),
  factor(
    'opponentDefense',
    'reboundingAllowed',
    'Rebounding Allowed',
    "Opponent's rebounding weakness",
    'factor_rebounding_allowed_enabled',
    PLAYER,
  ),
  factor(
    'opponentDefense',
    'threePointDefense',
    '3PT Defense',
    'Opponent 3PT% allowed',
    'factor_three_point_defense_enabled',
    PLAYER,
  ),
  factor(
    'opponentDefense',
    'pointsInPaint',
    'Points in Paint',
    'Opponent interior defense rating',
    'factor_points_in_paint_enabled',
    PLAYER,
  ),
  factor(
    'opponentDefense',
    'fastBreakAllowed',
    'Fast Break Allowed',
    'Opponent fast break points allowed',
    'factor_fast_break_allowed_enabled',
    PLAYER,
  ),

  // Game script
  factor(
    'gameScript',
    'blowoutRisk',
    'Blowout Risk',
    'Warn when large spreads may cause starters to rest',
    'factor_blowout_risk_enabled',
    ON,
  ),
  factor(
    'gameScript',
    'backToBack',
    'Back-to-Back Detection',
    'Flag games where teams played last night',
    'factor_b2b_enabled',
    ON,
  ),
  factor(
    'gameScript',
    'paceImpact',
    'Pace Impact',
    'Matchup pace analysis (fast/slow game environment)',
    'factor_pace_enabled',
  ),
  factor(
    'gameScript',
    'gameTotal',
    'Game Total',
    'Use O/U total to gauge scoring environment',
    'factor_game_total_enabled',
  ),
  factor(
    'gameScript',
    'homeAwaySplits',
    'Home/Away Splits',
    'Consider location-dependent performance patterns',
    'factor_home_away_splits_enabled',
    ON,
  ),
  factor(
    'gameScript',
    'revengeGame',
    'Revenge Game',
    'Factor in recent blowout loss motivation',
    'factor_revenge_game_enabled',
  ),
  factor(
    'gameScript',
    'recentForm',
    'Team Momentum',
    'Team L5 win/loss streaks, hot or cold (not player stats)',
    'factor_recent_form_enabled',
  ),

  // Efficiency metrics (betting history)
  factor(
    'efficiencyMetrics',
    'bettingValueEfficiency',
    'Betting Value Efficiency',
    'Analyze margin vs line (how much players beat/miss props)',
    'factor_betting_value_efficiency_enabled',
    { feature: 'history_community' },
  ),
  factor(
    'efficiencyMetrics',
    'lineValueDetection',
    'Line Value Detection',
    "Alert when lines diverge from player's stat baseline",
    'factor_line_value_detection_enabled',
    PLAYER,
  ),
  factor(
    'efficiencyMetrics',
    'historicalTrends',
    'Historical Trends',
    'Track improving/declining efficiency over time',
    'factor_historical_trends_enabled',
    { feature: 'history_community' },
  ),

  // Other factors
  factor(
    'otherFactors',
    'injuryStatus',
    'Injury Status',
    'Day-to-day / Questionable flags',
    'factor_injury_status_enabled',
    ON,
  ),
  factor(
    'otherFactors',
    'includeProjectedStarters',
    'Projected Starters',
    'Projected starting lineups for both teams',
    'factor_projected_starters_enabled',
    { ...PLAYER, dataSource: 'msf' },
  ),
  factor(
    'otherFactors',
    'usageTrend',
    'Usage Trend',
    'Detect usage rate changes (L5 vs L10)',
    'factor_usage_trend_enabled',
    {
      ...PLAYER,
      deprecatedReason: 'Disabled: depends on Usage Rate (deprecated)',
    },
  ),
  factor(
    'otherFactors',
    'minutesTrend',
    'Minutes Trend',
    'Detect playing time changes (L5 vs L10)',
    'factor_minutes_trend_enabled',
    PLAYER,
  ),
  factor(
    'otherFactors',
    'efficiencyTrend',
    'Efficiency Trend',
    'Detect shooting efficiency changes (FG%/3PT% L5 vs L10)',
    'factor_efficiency_trend_enabled',
    {
      ...PLAYER,
      deprecatedReason: 'Disabled: depends on True Shooting % (deprecated)',
    },
  ),
  factor(
    'otherFactors',
    'includeSharpContext',
    'Sharp Context in Prompt',
    'Include Pinnacle/ProphetX lines, +EV, and line movement signals in the AI prompt',
    'factor_sharp_context_enabled',
    { feature: 'sharp_money_signals' },
  ),
  factor(
    'otherFactors',
    'includeTeamPropSignals',
    'Team Prop Hit Rates in Prompt',
    "Add how our settled picks have hit against each opponent (e.g. 'Props vs BOS: 48% hit rate') to the AI prompt",
    null,
    { feature: 'team_prop_hit_rates' },
  ),
];

const BY_ID = new Map(FACTOR_REGISTRY.map((f) => [f.id, f]));

export function getFactor(id: string): FactorDefinition | undefined {
  return BY_ID.get(id);
}

export function factorsInSection(section: FactorSection): FactorDefinition[] {
  return FACTOR_REGISTRY.filter((f) => f.section === section);
}

/** Whether a factor is on in a config: the stored value when set, otherwise its registry default. */
export function isFactorEnabled(config: AnalysisConfig, id: string): boolean {
  const def = BY_ID.get(id);
  if (!def) return false;
  const value = (config[def.section] as Record<string, unknown> | undefined)?.[def.key];
  return typeof value === 'boolean' ? value : def.defaultEnabled;
}

/**
 * isFactorEnabled for code that holds one config section: the value when set, else the registry
 * default the config panel shows. Every prompt gate reads factors through this or isFactorEnabled.
 */
export function isSectionFactorEnabled(
  section: FactorSection,
  sectionConfig: object | undefined,
  key: string,
): boolean {
  return isFactorEnabled({ [section]: sectionConfig ?? {} }, `${section}.${key}`);
}

/** True when no tier but admin can see the feature. */
function isAdminOnly(feature: GatedFeature): boolean {
  return SUBSCRIPTION_TIERS.every(
    (tier) => tier === 'admin' || getAccessPattern(tier, feature) === 'hidden',
  );
}

/** Deprecated and admin-only factors do not count toward a tier's factor limit. */
export function countsTowardLimit(def: FactorDefinition): boolean {
  if (def.deprecatedReason) return false;
  return !def.feature || !isAdminOnly(def.feature);
}

/** Enabled factors that count toward the limit. */
export function countEnabledFactors(config: AnalysisConfig): number {
  return FACTOR_REGISTRY.filter((f) => countsTowardLimit(f) && isFactorEnabled(config, f.id))
    .length;
}

/** How many factors count toward the limit in all: the most any tier can turn on. */
export function countableFactorTotal(): number {
  return FACTOR_REGISTRY.filter(countsTowardLimit).length;
}

/** The registry defaults as the factor sections of an AnalysisConfig. */
export function defaultFactorSections(): Pick<AnalysisConfig, FactorSection> {
  const sections: Record<string, Record<string, boolean>> = {};
  for (const f of FACTOR_REGISTRY) {
    sections[f.section] ??= {};
    sections[f.section][f.key] = f.defaultEnabled;
  }
  return sections;
}

/** Whether a tier may use a factor at all (its gating key allows it, or it has none). */
export function canUseFactor(tier: SubscriptionTier, def: FactorDefinition): boolean {
  return !def.feature || getAccessPattern(tier, def.feature) === 'allowed';
}

/** canUseFactor by registry id; false for an id the registry does not have. */
export function canUseFactorById(tier: SubscriptionTier, factorId: string): boolean {
  const def = getFactor(factorId);
  return def !== undefined && canUseFactor(tier, def);
}

/** False when the factor's only data source is reported off; unknown sources count as on. */
export function isFactorAvailable(def: FactorDefinition, sources: FactorDataSources): boolean {
  return !def.dataSource || sources[def.dataSource] !== false;
}
